/**
 * The structured answer for one agent turn. JSON-serializable, raw numbers
 * rather than formatted strings, citations by handle.
 */

export type AgentAnswerPhase = "thinking" | "answering" | "done" | "error" | "aborted";

export type AgentStepStatus = "running" | "complete" | "error";

export interface IAgentProgressStep {
    /** The server's `call_id`. */
    id: string;
    /** Human-readable, e.g. "Reading bills". */
    label: string;
    status: AgentStepStatus;
    /** The server's tool name, or null when it is one this client doesn't know. */
    tool: string | null;
    elapsedMs: number | null;
}

export type AgentProseSegment =
    | { kind: "text"; text: string }
    | { kind: "citation"; citationId: string };

export interface IAgentCitation {
    /** 1-based, in first-cited order. */
    id: string;
    /** The only routing key. */
    handle: string;
    label: string;
    amount: number | null;
}

export interface IAgentAnswer {
    /** Correlates this turn's telemetry. */
    turnId: string;
    /**
     * The session this turn ran under, once its stream opened. Kept on the
     * turn because Stop, a timeout or a lost session start a new one mid-
     * conversation, so the live session no longer says which ran this turn.
     */
    sessionId?: string | null;
    question: string;
    phase: AgentAnswerPhase;
    steps: IAgentProgressStep[];
    /** The final answer text, split on blank lines. */
    paragraphs: AgentProseSegment[][];
    citations: Record<string, IAgentCitation>;
    error: string | null;
}

/** A record the agent named, minted while executing its tools. */
export interface IAgentHandleRecord {
    entity: string;
    id: string;
    summary?: string;
    payload?: Record<string, unknown>;
}

export interface IAgentToolCall {
    /** The server's `call_id`, which `tool_end` echoes. */
    id: string;
    name: string;
    /** Whatever the model passed. Raw, because the tool set is the server's. */
    input: Record<string, unknown>;
}

export function emptyAnswer(input: { turnId: string; question: string }): IAgentAnswer {
    return {
        turnId: input.turnId,
        question: input.question,
        phase: "thinking",
        steps: [],
        paragraphs: [],
        citations: {},
        error: null,
    };
}
